import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PAYMENT_METHODS } from './constants';

// Series MVP allows the same offline methods as single manual bookings
// (cash | bank_transfer | complimentary | pay_later). Pocket Pay (online
// charge) is excluded — refund complexity for paid recurring series remains
// out of scope. Module-level constant (not component-local) so its reference
// is stable across renders for the useEffect dependency below.
const SERIES_METHODS = ['cash', 'bank_transfer', 'complimentary', 'pay_later'];

export default function PaymentDiscountCard({
  paymentMethod, setPaymentMethod,
  paymentReference, setPaymentReference,
  discountType, setDiscountType,
  discountValue, setDiscountValue,
  discountReason, setDiscountReason,
  // When this flag is on, the picker hides any other methods and snaps
  // non-allowed selections back to cash.
  restrictedToSeriesMethods = false,
}) {
  const methods = restrictedToSeriesMethods
    ? PAYMENT_METHODS.filter((p) => SERIES_METHODS.includes(p.value))
    : PAYMENT_METHODS;

  // If switching into restricted mode while a non-allowed method is active, snap back to cash
  React.useEffect(() => {
    if (restrictedToSeriesMethods && !SERIES_METHODS.includes(paymentMethod)) {
      setPaymentMethod('cash');
    }
  }, [restrictedToSeriesMethods, paymentMethod, setPaymentMethod]);

  return (
    <Card className="p-4 space-y-4">
      <div>
        <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Payment Method</Label>
        <div className="flex flex-wrap gap-2">
          {methods.map((p) => {
            const Icon = p.icon;
            const active = paymentMethod === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setPaymentMethod(p.value)}
                data-testid={`payment-${p.value}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" /> {p.label}
              </button>
            );
          })}
        </div>
        <Input
          className="mt-2"
          placeholder={paymentMethod === 'pay_later'
            ? 'No reference needed (booking is held)'
            : 'Reference (optional) e.g. transfer slip / cheque #'}
          value={paymentReference}
          onChange={(e) => setPaymentReference(e.target.value)}
          data-testid="payment-ref"
          disabled={paymentMethod === 'pay_later'}
        />
        {paymentMethod === 'pay_later' && (
          <div
            className="mt-2 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm"
            data-testid="pay-later-notice"
          >
            <strong>⏳ Pay Later (Hold)</strong> — Booking will be confirmed
            but held with status <em>pending payment</em>. The customer
            receives a "Payment Required" email with bank/online options.
            Use <strong>Mark as Paid</strong> or <strong>Release Slot</strong>{' '}
            on the Bookings page when the customer pays or no-shows.
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs uppercase font-bold text-muted-foreground mb-2 block">Discount (optional)</Label>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={discountType} onValueChange={setDiscountType}>
            <SelectTrigger className="w-28" data-testid="discount-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">%</SelectItem>
              <SelectItem value="fixed">$</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            min="0"
            placeholder="Value"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            className="w-32"
            data-testid="discount-value"
          />
          <Input
            placeholder="Reason (e.g. Birthday promo)"
            value={discountReason}
            onChange={(e) => setDiscountReason(e.target.value)}
            className="flex-1 min-w-[200px]"
            data-testid="discount-reason"
          />
        </div>
      </div>
    </Card>
  );
}
